import React, { useEffect, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
    apiService,
    Appointment,
    IntakeAdminResponse,
    IntakeFormAdmin,
} from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

interface Props {
    appointment: Appointment;
}

const PUBLIC_SITE_BASE = process.env.NODE_ENV === 'production'
    ? 'https://seadent.es'
    : 'http://localhost:3001';

// ---------- small helpers ----------

const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
};

const yesNoLabel = (v: string | null | undefined): { label: string; color: string } => {
    if (v === 'yes') return { label: 'Ja', color: 'bg-red-100 text-red-800 border-red-200' };
    if (v === 'no') return { label: 'Nein', color: 'bg-green-100 text-green-800 border-green-200' };
    if (v === 'unsure') return { label: 'Unsicher', color: 'bg-amber-100 text-amber-800 border-amber-200' };
    return { label: '—', color: 'bg-gray-100 text-gray-500 border-gray-200' };
};

const PERSONAL_LABELS: Record<string, string> = {
    name: 'Name', street: 'Straße', postal_code: 'PLZ', city: 'Ort', country: 'Land',
    email: 'E-Mail', date_of_birth: 'Geburtsdatum', place_of_birth: 'Geburtsort',
    phone_private: 'Tel. privat', phone_work: 'Tel. dienstlich',
    occupation: 'Beruf', nie: 'N.I.E.',
    doctor_name: 'Hausarzt', doctor_address: 'Anschrift', doctor_phone: 'Telefon',
};

const FLAG_LABELS: Record<string, string> = {
    high_blood_pressure: 'Hoher Blutdruck', low_blood_pressure: 'Niedriger Blutdruck',
    heart_valve_disease: 'Herzklappenfehler', heart_valve_replacement: 'Herzklappenersatz',
    pacemaker: 'Herzschrittmacher', endocarditis: 'Endokarditis', heart_surgery: 'Herzoperation',
    epilepsy: 'Epilepsie', asthma: 'Asthma / Lunge', blood_clotting: 'Blutgerinnung',
    diabetes: 'Diabetes', drug_dependency: 'Drogen', nerve_disease: 'Nervenerkrankung',
    kidney_disease: 'Nieren', fainting_spells: 'Ohnmachtsanfälle', osteoporosis: 'Osteoporose',
    severe_neutropenia: 'Neutropenie', cystic_fibrosis: 'Mukoviszidose',
    organ_transplant: 'Organtransplantat', stem_cell_transplant: 'Stammzelltransplantat',
    smoker: 'Raucher', rheumatism: 'Rheuma', thyroid_disease: 'Schilddrüse',
    other_diseases: 'Sonstige',
    hiv: 'HIV/AIDS', hepatitis: 'Hepatitis', tuberculosis: 'Tuberkulose',
    other_infectious: 'Sonstige infektiös',
    local_anesthesia: 'Lokalanästhesie', antibiotics: 'Antibiotika', pain_medication: 'Schmerzmittel',
    bisphosphonates: 'Bisphosphonate', chemotherapy: 'Chemotherapie',
    radiation_therapy: 'Strahlentherapie', steroids: 'Steroide',
};

// ---------- mail-status badge ----------

const MailStatusBadge: React.FC<{ status: 'pending' | 'sent' | 'failed' | undefined }> = ({ status }) => {
    if (status === 'sent') {
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            E-Mail versendet
        </span>;
    }
    if (status === 'failed') {
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            E-Mail fehlgeschlagen
        </span>;
    }
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
        E-Mail ausstehend
    </span>;
};

// ---------- intake-status badge ----------

const IntakeStatusBadge: React.FC<{ submitted: boolean }> = ({ submitted }) => (
    submitted
        ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            Bogen ausgefüllt
        </span>
        : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
            Wartet auf Patient
        </span>
);

// ---------- main panel ----------

const SUPPORTED_LANGS: Array<{ code: 'de' | 'en' | 'es' | 'ru'; label: string }> = [
    { code: 'de', label: 'Deutsch' },
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'ru', label: 'Русский' },
];

const IntakeAdminPanel: React.FC<Props> = ({ appointment }) => {
    const { addToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<IntakeAdminResponse | null>(null);
    const [resending, setResending] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [showAllAnswers, setShowAllAnswers] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    // Patient-invitation form state. Prefill from the appointment record;
    // admin can edit before sending.
    const initialLang = (appointment.language || 'de').toLowerCase();
    const normalizedInitialLang = (SUPPORTED_LANGS.find((l) => l.code === initialLang)?.code) || 'de';
    const [inviteEmail, setInviteEmail] = useState<string>(appointment.patient_email || '');
    const [inviteLang, setInviteLang] = useState<'de' | 'en' | 'es' | 'ru'>(normalizedInitialLang);
    const [sendingInvite, setSendingInvite] = useState(false);
    const [inviteLastSentTo, setInviteLastSentTo] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await apiService.getIntake(appointment.id);
            setData(res.data);
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Fehler beim Laden';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [appointment.id]);

    useEffect(() => { load(); }, [load]);

    const intakeUrl = data ? `${PUBLIC_SITE_BASE}/intake/${data.appointment.intake_token}` : '';

    const copyLink = async () => {
        if (!intakeUrl) return;
        try {
            await navigator.clipboard.writeText(intakeUrl);
            setLinkCopied(true);
            addToast('Link in die Zwischenablage kopiert', 'success');
            setTimeout(() => setLinkCopied(false), 2500);
        } catch {
            addToast('Konnte Link nicht kopieren', 'error');
        }
    };

    const handleSendInvitation = async () => {
        const to = inviteEmail.trim();
        if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
            addToast('Bitte eine gültige E-Mail-Adresse eingeben', 'error');
            return;
        }
        try {
            setSendingInvite(true);
            const res = await apiService.sendIntakeInvitation(appointment.id, { to, language: inviteLang });
            if (res.data.success) {
                addToast(`Einladung an ${res.data.mail_to || to} versendet`, 'success');
                setInviteLastSentTo(res.data.mail_to || to);
            } else {
                addToast(`Versand fehlgeschlagen: ${res.data.error || 'Unbekannt'}`, 'error');
            }
        } catch (err: any) {
            const msg = err?.response?.data?.error
                || err?.response?.data?.message
                || 'Versand fehlgeschlagen';
            addToast(msg, 'error');
        } finally {
            setSendingInvite(false);
        }
    };

    const handleResend = async () => {
        try {
            setResending(true);
            const res = await apiService.resendIntakeMail(appointment.id);
            if (res.data.success) {
                addToast(`E-Mail an ${res.data.mail_to} versendet`, 'success');
            } else {
                addToast(`Mail fehlgeschlagen: ${res.data.error || 'Unbekannt'}`, 'error');
            }
            await load();
        } catch (err: any) {
            addToast(err?.response?.data?.message || 'Resend fehlgeschlagen', 'error');
        } finally {
            setResending(false);
        }
    };

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const blob = await apiService.fetchIntakePdf(appointment.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `intake-${appointment.id}-${appointment.patient_name.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err: any) {
            addToast(err?.response?.data?.message || 'PDF-Download fehlgeschlagen', 'error');
        } finally {
            setDownloading(false);
        }
    };

    const handleOpenPdf = async () => {
        try {
            setDownloading(true);
            const blob = await apiService.fetchIntakePdf(appointment.id);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            // Don't revoke immediately — give the new tab a moment to start loading.
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err: any) {
            addToast(err?.response?.data?.message || 'PDF konnte nicht geöffnet werden', 'error');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return <div className="py-8 text-center text-gray-500">Lädt …</div>;
    }
    if (error || !data) {
        return <div className="py-6 px-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
            {error || 'Keine Daten'}
        </div>;
    }

    const form = data.form;
    const submitted = data.appointment.intake_status === 'submitted';

    return (
        <div className="space-y-5">
            {/* Top status row */}
            <div className="flex flex-wrap items-center gap-2">
                <IntakeStatusBadge submitted={submitted} />
                {form && <MailStatusBadge status={form.mail_status} />}
                {form && form.mail_attempts > 0 && (
                    <span className="text-xs text-gray-500">
                        Versuche: {form.mail_attempts} · zuletzt {formatDateTime(form.mail_last_at)}
                    </span>
                )}
            </div>

            {/* Mail-error banner — surfaced prominently when send failed */}
            {form && form.mail_status === 'failed' && form.mail_last_error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <div className="font-semibold mb-1">E-Mail an {form.mail_to || 'info@seadent.es'} konnte nicht versendet werden:</div>
                    <code className="text-xs break-all">{form.mail_last_error}</code>
                </div>
            )}

            {/* Always-available admin actions: link, QR, send */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
                <div className="space-y-3 min-w-0">
                    <div>
                        <div className="text-xs font-medium text-gray-600 mb-1">Patient-Link</div>
                        <div className="flex gap-2">
                            <input
                                readOnly
                                value={intakeUrl}
                                onFocus={(e) => e.currentTarget.select()}
                                className="flex-1 min-w-0 px-3 py-2 text-sm font-mono bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                                onClick={copyLink}
                                className="px-3 py-2 text-sm font-medium text-white bg-[#384860] hover:bg-[#263245] rounded-md transition-colors whitespace-nowrap"
                            >
                                {linkCopied ? 'Kopiert!' : 'Link kopieren'}
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {submitted && form?.pdf_path && (
                            <>
                                <button
                                    onClick={handleOpenPdf}
                                    disabled={downloading}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#384860] bg-white border border-[#384860]/30 hover:bg-[#384860]/5 rounded-md disabled:opacity-50 transition-colors"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    PDF öffnen
                                </button>
                                <button
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#384860] bg-white border border-[#384860]/30 hover:bg-[#384860]/5 rounded-md disabled:opacity-50 transition-colors"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    PDF herunterladen
                                </button>
                            </>
                        )}
                        {submitted && (
                            <button
                                onClick={handleResend}
                                disabled={resending}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${form?.mail_status === 'failed' ? 'text-white bg-[#ff6f59] hover:bg-[#e55a45]' : 'text-[#384860] bg-white border border-[#384860]/30 hover:bg-[#384860]/5'}`}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><polyline points="21 3 21 8 16 8"/></svg>
                                {resending ? 'Sende …' : form?.mail_status === 'failed' ? 'Erneut senden' : 'E-Mail erneut senden'}
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-center justify-center bg-white p-3 rounded-lg border border-gray-200">
                    <QRCodeSVG value={intakeUrl} size={104} level="M" />
                    <span className="text-[10px] text-gray-500 mt-1">QR-Code</span>
                </div>
            </div>

            {/* Patient-invitation: send link + QR by mail in patient's language */}
            <div className="p-4 rounded-lg border border-gray-200 bg-white">
                <div className="flex items-start gap-2 mb-3">
                    <div className="shrink-0 mt-0.5 text-[#93cbb6]">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Link an Patient senden</h3>
                        <p className="text-xs text-gray-500">Der Patient erhält eine freundliche E-Mail mit Link und QR-Code — kann den Bogen direkt auf dem Handy ausfüllen.</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
                    <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="patient@example.com"
                        className="min-w-0 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <select
                        value={inviteLang}
                        onChange={(e) => setInviteLang(e.target.value as 'de' | 'en' | 'es' | 'ru')}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        {SUPPORTED_LANGS.map((l) => (
                            <option key={l.code} value={l.code}>{l.label}</option>
                        ))}
                    </select>
                    <button
                        onClick={handleSendInvitation}
                        disabled={sendingInvite || !inviteEmail.trim()}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#93cbb6] hover:bg-[#7eb9a1] disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors whitespace-nowrap"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                        {sendingInvite ? 'Sende …' : 'An Patient senden'}
                    </button>
                </div>
                {inviteLastSentTo && (
                    <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        Zuletzt versendet an <span className="font-medium">{inviteLastSentTo}</span>
                    </div>
                )}
            </div>

            {/* Submitted form readout */}
            {submitted && form && (
                <FormReadout
                    form={form}
                    expanded={showAllAnswers}
                    onToggleExpand={() => setShowAllAnswers((v) => !v)}
                />
            )}

            {!submitted && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                    Der Patient hat den Bogen noch nicht ausgefüllt. Schicken Sie den Link per E-Mail oder zeigen Sie den QR-Code in der Praxis.
                </div>
            )}
        </div>
    );
};

// ---------- form readout (read-only) ----------

const FormReadout: React.FC<{
    form: IntakeFormAdmin;
    expanded: boolean;
    onToggleExpand: () => void;
}> = ({ form, expanded, onToggleExpand }) => {
    const renderFlagGroup = (
        title: string,
        flags: Record<string, 'yes' | 'no' | 'unsure' | null>,
        order: string[],
    ) => {
        // Compact view: only show flags answered "yes" or "unsure" — typical
        // medical chart workflow (the "no" answers are noise).
        const visible = expanded
            ? order.filter((k) => flags[k] !== undefined)
            : order.filter((k) => flags[k] === 'yes' || flags[k] === 'unsure');
        if (visible.length === 0) return null;
        return (
            <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {visible.map((flag) => {
                        const { label, color } = yesNoLabel(flags[flag]);
                        return (
                            <div key={flag} className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
                                <span className="text-sm text-gray-700 truncate">{FLAG_LABELS[flag] || flag}</span>
                                <span className={`shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full border ${color}`}>{label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const flagOrder = {
        cardiovascular: ['high_blood_pressure', 'low_blood_pressure', 'heart_valve_disease', 'heart_valve_replacement', 'pacemaker', 'endocarditis', 'heart_surgery'],
        general: ['epilepsy', 'asthma', 'blood_clotting', 'diabetes', 'drug_dependency', 'nerve_disease', 'kidney_disease', 'fainting_spells', 'osteoporosis', 'severe_neutropenia', 'cystic_fibrosis', 'organ_transplant', 'stem_cell_transplant', 'smoker', 'rheumatism', 'thyroid_disease', 'other_diseases'],
        infectious: ['hiv', 'hepatitis', 'tuberculosis', 'other_infectious'],
        allergies: ['local_anesthesia', 'antibiotics', 'pain_medication'],
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Antworten des Patienten</h3>
                <button
                    onClick={onToggleExpand}
                    className="text-xs font-medium text-[#384860] hover:text-[#263245] underline-offset-2 hover:underline"
                >
                    {expanded ? 'Nur auffällige Antworten zeigen' : 'Alle Antworten zeigen'}
                </button>
            </div>

            {/* Submission meta */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div>
                    <div className="text-xs text-gray-500">Eingereicht</div>
                    <div className="font-medium text-gray-900">{formatDateTime(form.submitted_at)}</div>
                </div>
                <div>
                    <div className="text-xs text-gray-500">Unterschrift</div>
                    <div className="font-medium text-gray-900">{form.signed_place || '—'}</div>
                </div>
                <div>
                    <div className="text-xs text-gray-500">Sprache</div>
                    <div className="font-medium text-gray-900 uppercase">{form.language}</div>
                </div>
            </div>

            {/* Personal data — compact 2-column */}
            <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Stammdaten</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    {Object.entries(form.personal || {})
                        .filter(([, v]) => v && String(v).trim().length > 0)
                        .map(([k, v]) => (
                            <div key={k} className="flex">
                                <span className="w-32 shrink-0 text-gray-500">{PERSONAL_LABELS[k] || k}</span>
                                <span className="text-gray-900 break-words">{v}</span>
                            </div>
                        ))}
                </div>
            </div>

            {/* Highlight findings: yes-answered flags */}
            {renderFlagGroup('Herz/Kreislauf', form.health.cardiovascular || {}, flagOrder.cardiovascular)}
            {renderFlagGroup('Weitere Erkrankungen', form.health.general || {}, flagOrder.general)}
            {form.other_diseases_text && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm">
                    <span className="font-medium text-amber-900">Sonstige Erkrankungen: </span>
                    <span className="text-amber-800">{form.other_diseases_text}</span>
                </div>
            )}

            {renderFlagGroup('Infektiöse Erkrankungen', form.health.infectious || {}, flagOrder.infectious)}
            {form.other_infectious_text && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm">
                    <span className="font-medium text-amber-900">Andere infektiös: </span>
                    <span className="text-amber-800">{form.other_infectious_text}</span>
                </div>
            )}

            {renderFlagGroup('Allergien', form.health.allergies || {}, flagOrder.allergies)}
            {form.metals_allergy_text && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm">
                    <span className="font-medium text-amber-900">Metall-Allergie: </span>
                    <span className="text-amber-800">{form.metals_allergy_text}</span>
                </div>
            )}

            {/* Pregnancy / xray / surgery */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {form.pregnancy.is_pregnant === 'yes' && (
                    <div className="px-3 py-2 bg-pink-50 border border-pink-200 rounded">
                        <span className="font-medium text-pink-900">Schwanger:</span>{' '}
                        <span className="text-pink-800">Monat {form.pregnancy.month || '—'}</span>
                    </div>
                )}
                {form.major_surgery.value === 'yes' && (
                    <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded">
                        <span className="font-medium text-orange-900">Große OP:</span>{' '}
                        <span className="text-orange-800">{form.major_surgery.date || '—'}</span>
                    </div>
                )}
                {form.xray.had_xray === 'yes' && (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded">
                        <span className="font-medium text-gray-700">Zahn-Röntgen:</span>{' '}
                        <span className="text-gray-800">{form.xray.when || '—'}</span>
                    </div>
                )}
            </div>

            {/* Medications */}
            {form.medications.length > 0 && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Medikamente</h4>
                    <ul className="text-sm space-y-0.5">
                        {form.medications.map((m, i) => (
                            <li key={i}>
                                <span className="text-gray-900 font-medium">{m.name}</span>
                                {m.since && <span className="text-gray-500"> · seit {m.since}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Special meds — show only the "yes" answers */}
            {Object.entries(form.special_medications || {})
                .filter(([, v]) => v?.value === 'yes')
                .length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1.5">Spezielle Therapien</h4>
                    <ul className="text-sm space-y-0.5">
                        {Object.entries(form.special_medications || {})
                            .filter(([, v]) => v?.value === 'yes')
                            .map(([k, v]) => (
                                <li key={k}>
                                    <span className="text-red-900 font-medium">{FLAG_LABELS[k] || k}</span>
                                    {v.since && <span className="text-red-700"> · seit {v.since}</span>}
                                </li>
                            ))}
                    </ul>
                </div>
            )}

            {/* Signature */}
            {form.signature_data_url && (
                <div className="p-3 bg-white border border-gray-200 rounded">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Unterschrift</h4>
                    <img
                        src={form.signature_data_url}
                        alt="Unterschrift"
                        className="max-h-24 object-contain"
                        style={{ filter: 'invert(0)' }}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                        {form.signed_place || '—'} · {formatDateTime(form.signed_at)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntakeAdminPanel;
